import CodeRoundedIcon from "@mui/icons-material/CodeRounded";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import { Menu } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { ReportAccessType } from "@entrilia/shared/api/reportAccessTypes.ts";
import ActionMenuItem from "@entrilia/shared/components/ActionMenuItem";
import AuthorizedBox from "@entrilia/shared/components/AuthorizedBox";
import ReportAuthorizedBox from "@entrilia/shared/components/ReportAuthorizedBox";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import DuplicateReportIcon from "@entrilia/shared/icons/DuplicateReportIcon";
import type { ReportInfo } from "../../../../../../api/types/svTypes.ts";
import { ReportType } from "../../../../../../api/types/svTypes.ts";
import { useReportsManagerContext } from "../../contexts/reports-manager/ReportsManagerContext";
import { useReportsContext } from "../../contexts/reports/ReportsContext";
import ActionMenuItemLink from "../ActionMenuItemLink";
import { CopyToMenuItem } from "../copyto/CopyToMenuItem";
import { canCopyReport } from "../grid/toolbarActionsHelper.ts";
import { withVersionHistoryParam } from "../utilities/editReportUrl";
import ChangeReportGroup from "./ChangeReportGroup.tsx";
import RenameReport from "./RenameReport.tsx";

interface Props {
  anchorEl: HTMLElement | null;
  report: ReportInfo;
  open: boolean;
  openInNewTab?: boolean;
  onRun: (reportId: string) => string;
  onEdit: (reportId: string) => string;
  onClose: () => void;
}

export default function ReportActions({ anchorEl, report, open, openInNewTab, onRun, onEdit, onClose }: Props) {
  const { sendNotification } = useNotificationContext();
  const { actions } = useReportsContext();
  const { copyReports, deleteReports, duplicateReport, shareReport, generateApiReport } = useReportsManagerContext();

  const [openRenameDialog, setOpenRenameDialog] = useState<boolean>();
  const [openChangeLevelDialog, setOpenChangeLevelDialog] = useState<boolean>();

  const getRunReportHref = useMemo(() => {
    return onRun(report.reportId);
  }, [report.reportId, onRun]);

  const getEditReportHref = useMemo(() => {
    return onEdit(report.reportId);
  }, [report.reportId, onEdit]);

  const handleDeleteReport = useCallback(() => {
    onClose();
    deleteReports([report.reportId]);
  }, [deleteReports, onClose, report.reportId]);

  const handleGenerateApiReport = useCallback(() => {
    onClose();
    generateApiReport?.(report);
  }, [generateApiReport, onClose, report]);

  const handleRenameReport = useCallback(() => {
    onClose();
    setOpenRenameDialog(true);
  }, [onClose]);

  const handleChangeLevelReport = useCallback(() => {
    onClose();
    setOpenChangeLevelDialog(true);
  }, [onClose]);

  const handleCopyToReport = useCallback(() => {
    onClose();
    copyReports([report.reportId]);
  }, [copyReports, onClose, report.reportId]);

  const handleDuplicateReport = useCallback(() => {
    onClose();
    duplicateReport(report);
  }, [duplicateReport, onClose, report]);

  const handleShareReport = useCallback(() => {
    onClose();
    shareReport(report);
  }, [shareReport, onClose, report]);

  return (
    <>
      <Menu anchorEl={anchorEl} open={open} onClose={onClose} slotProps={{ paper: { sx: { width: "200px" } } }}>
        <ActionMenuItemLink
          key={"run"}
          icon={<PlayArrowRoundedIcon />}
          title="Run Report"
          href={getRunReportHref}
          openInNewTab={openInNewTab}
          onClick={onClose}
        />
        <ReportAuthorizedBox
          clientCode={report.clientCode}
          permissions={["ManageReports"]}
          requiredAccess={ReportAccessType.Write}
          access={report.authorization.access}
        >
          <ActionMenuItemLink
            key={"edit"}
            icon={<EditOutlinedIcon />}
            title="Edit Report"
            href={getEditReportHref}
            openInNewTab={openInNewTab}
            onClick={onClose}
          />
          <ActionMenuItem
            key={"rename"}
            icon={<DriveFileRenameOutlineOutlinedIcon />}
            title="Rename"
            onClick={handleRenameReport}
          />
        </ReportAuthorizedBox>
        <ReportAuthorizedBox
          clientCode={report.clientCode}
          permissions={report.inheritFromParent ? ["ShareReports"] : ["ManageReports"]}
          requiredAccess={report.inheritFromParent ? ReportAccessType.FullAccess : ReportAccessType.Write}
          access={report.authorization.access}
        >
          <ActionMenuItem
            key={"changeLevel"}
            icon={<DriveFileMoveOutlinedIcon />}
            title="Change Group"
            onClick={handleChangeLevelReport}
          />
        </ReportAuthorizedBox>
        <AuthorizedBox clientCode={report.clientCode} permissions={["ManageReports"]}>
          {canCopyReport(report) && <CopyToMenuItem onClick={handleCopyToReport} />}
          <ActionMenuItem
            key={"duplicate"}
            icon={<DuplicateReportIcon />}
            title="Duplicate"
            onClick={handleDuplicateReport}
          />
        </AuthorizedBox>
        <ReportAuthorizedBox
          clientCode={report.clientCode}
          access={report.authorization.access}
          permissions={["ShareReports"]}
          requiredAccess={ReportAccessType.FullAccess}
        >
          <ActionMenuItem key={"share"} icon={<ShareOutlinedIcon />} title="Share" onClick={handleShareReport} />
        </ReportAuthorizedBox>
        {report.reportUsage === "Ui" && report.reportType === ReportType.Tabular && (
          <ReportAuthorizedBox
            clientCode={report.clientCode}
            permissions={["ManageReports"]}
            access={report.authorization.access}
            requiredAccess={ReportAccessType.Write}
          >
            <ActionMenuItem
              key={"generateApi"}
              icon={<CodeRoundedIcon />}
              title="Generate API Report"
              onClick={handleGenerateApiReport}
            />
          </ReportAuthorizedBox>
        )}
        <ReportAuthorizedBox
          clientCode={report.clientCode}
          permissions={["ManageReports"]}
          access={report.authorization.access}
          requiredAccess={ReportAccessType.Write}
        >
          <ActionMenuItemLink
            key={"versionHistory"}
            icon={<HistoryOutlinedIcon />}
            title="Version history"
            href={withVersionHistoryParam(getEditReportHref)}
            openInNewTab={openInNewTab}
            onClick={onClose}
          />
        </ReportAuthorizedBox>
        <ReportAuthorizedBox
          clientCode={report.clientCode}
          permissions={["ManageReports"]}
          access={report.authorization.access}
          requiredAccess={ReportAccessType.Owner}
        >
          <ActionMenuItem
            key={"delete"}
            icon={<DeleteOutlineOutlinedIcon color="error" />}
            title="Delete"
            onClick={handleDeleteReport}
          />
        </ReportAuthorizedBox>
      </Menu>
      {openRenameDialog && report && (
        <RenameReport
          report={report}
          renameReport={actions.renameReport}
          onChanged={() => sendNotification("The report was successfully renamed.")}
          onClose={() => setOpenRenameDialog(false)}
        />
      )}
      {openChangeLevelDialog && report && (
        <ChangeReportGroup
          report={report}
          changeGroup={actions.changeReportGroup}
          onChanged={() => sendNotification("The group was changed for the selected report.")}
          onClose={() => setOpenChangeLevelDialog(false)}
        />
      )}
    </>
  );
}
